import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ArrowLeft, Plus, Edit, Trash2, HelpCircle, Save, Loader2 } from 'lucide-react';
import { faqsAPI } from '../../services/api';

const AdminFAQ = () => {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [formData, setFormData] = useState({ question: '', answer: '', featured: false });
  const [message, setMessage] = useState('');

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin/login');
      return;
    }
    loadFaqs();
  }, [navigate]);

  const loadFaqs = async () => {
    setLoading(true);
    try {
      const data = await faqsAPI.getAll();
      setFaqs(data);
    } catch (error) {
      console.error('Erro ao carregar FAQs:', error);
      setMessage('❌ Erro ao carregar FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (faq = null) => {
    if (faq) {
      setEditingFaq(faq);
      setFormData({ question: faq.question, answer: faq.answer, featured: faq.featured });
    } else {
      setEditingFaq(null);
      setFormData({ question: '', answer: '', featured: false });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.question.trim() || !formData.answer.trim()) {
      alert('Preencha a pergunta e a resposta');
      return;
    }

    setSaving(true);
    setMessage('');
    try {
      if (editingFaq) {
        await faqsAPI.update(editingFaq.id, formData);
        setMessage('✅ FAQ atualizada com sucesso!');
      } else {
        await faqsAPI.create(formData);
        setMessage('✅ FAQ criada com sucesso!');
      }
      
      await loadFaqs();
      setShowDialog(false);
      setEditingFaq(null);
      setFormData({ question: '', answer: '', featured: false });
    } catch (error) {
      console.error('Erro ao salvar FAQ:', error);
      setMessage('❌ Erro ao salvar FAQ');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta pergunta?')) return;
    
    try {
      await faqsAPI.delete(id);
      setMessage('✅ FAQ excluída com sucesso!');
      await loadFaqs();
    } catch (error) {
      console.error('Erro ao excluir FAQ:', error);
      setMessage('❌ Erro ao excluir FAQ');
    }
  };

  const toggleFeatured = async (faq) => {
    try {
      await faqsAPI.update(faq.id, { featured: !faq.featured });
      await loadFaqs();
    } catch (error) {
      console.error('Erro ao atualizar destaque:', error);
    }
  };

  const featuredFaqs = faqs.filter(f => f.featured);
  const otherFaqs = faqs.filter(f => !f.featured);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#003366] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin/dashboard" className="hover:text-[#FFD700]">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <HelpCircle className="w-6 h-6" />
            <h1 className="text-xl font-bold">Perguntas Frequentes (FAQ)</h1>
          </div>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-[#FFD700] hover:bg-[#FFC700] text-gray-900"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Pergunta
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {message && (
          <div className={`mb-4 p-4 rounded-lg ${message.includes('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {message}
          </div>
        )}

        {/* Info */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-800 text-sm">
            <strong>Destaque:</strong> As perguntas marcadas como "Destaque" aparecerão na página de Contato. 
            As alterações são salvas automaticamente no banco de dados.
          </p>
        </div>

        {/* FAQs em Destaque */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <span className="w-6 h-6 bg-[#FFD700] rounded-full flex items-center justify-center text-sm font-bold mr-2">
              {featuredFaqs.length}
            </span>
            Perguntas em Destaque (Página Contato)
          </h2>
          
          {featuredFaqs.length === 0 ? (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
              Nenhuma pergunta em destaque. Marque perguntas como destaque para aparecerem na página de contato.
            </div>
          ) : (
            <div className="space-y-3">
              {featuredFaqs.map((faq, index) => (
                <Card key={faq.id} className="border-l-4 border-l-[#FFD700]">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-xs bg-[#FFD700] text-gray-900 px-2 py-0.5 rounded font-medium">
                            #{index + 1}
                          </span>
                          <h3 className="font-semibold text-gray-900">{faq.question}</h3>
                        </div>
                        <p className="text-sm text-gray-600">{faq.answer}</p>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleFeatured(faq)}
                          className="text-yellow-600"
                          title="Remover do destaque"
                        >
                          ★
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(faq)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(faq.id)} className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Outras FAQs */}
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <span className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center text-sm font-bold mr-2">
              {otherFaqs.length}
            </span>
            Outras Perguntas (Disponíveis para busca)
          </h2>
          
          {otherFaqs.length === 0 ? (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
              Todas as perguntas estão em destaque ou não há outras perguntas cadastradas.
            </div>
          ) : (
            <div className="space-y-3">
              {otherFaqs.map((faq) => (
                <Card key={faq.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">{faq.question}</h3>
                        <p className="text-sm text-gray-600">{faq.answer}</p>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleFeatured(faq)}
                          className="text-gray-400 hover:text-yellow-600"
                          title="Adicionar ao destaque"
                        >
                          ☆
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(faq)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(faq.id)} className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dialog para adicionar/editar */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingFaq ? 'Editar Pergunta' : 'Nova Pergunta'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Pergunta *</Label>
              <Input
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                placeholder="Ex: Como faço para reservar um pacote?"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Resposta *</Label>
              <Textarea
                value={formData.answer}
                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                placeholder="Escreva a resposta..."
                className="mt-1"
                rows={4}
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="featured"
                checked={formData.featured}
                onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="featured">Mostrar em destaque na página de Contato</Label>
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#003366] hover:bg-[#002244] text-white"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvando...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" />Salvar</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFAQ;
